import { createFaq, editFaq, getFaq } from '@/communication/faq';
import Button from '@/components/buttons';
import InputField from '@/components/inputs/inputField';
import { useFetch } from '@/hooks/useFetch';
import { ServerError } from '@/types/communication/serverResponse';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { TextEditor } from '@/components/textEditor';
import { BackButton } from '@/components/buttons/backButton';

interface Props {
    createMode?: boolean;
}

interface FormValues {
    title: string;
    answer: string;
    published: number | null;
    sort: number | null;
}

const FaqDetails: FunctionComponent<Props> = (props) => {
    const { createMode } = props;
    const { faqId: faqIdParam } = useParams();
    const faqId = Number(faqIdParam);
    const nav = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formValues, setFormValues] = useState<FormValues>({ title: '', answer: '', published: 0, sort: 0 });
    const [validationErrors, setValidationErrors] = useState<any>();
    const { data, status } = useFetch({
        queryFn: () => getFaq(faqId),
        disabled: Number.isNaN(faqId),
    });

    const handleChange = (fieldTitle: keyof FormValues, value): void => {
        setFormValues((prevValues) => ({ ...prevValues, [fieldTitle]: value }));
    };

    const handleSubmit = async (): Promise<void> => {
        setIsSubmitting(true);
        setValidationErrors(null);

        try {
            await (createMode ? createFaq(formValues) : editFaq({ ...formValues, id: faqId }));
            nav('/faqs');
        } catch (error) {
            const serverError = error as ServerError;
            if (serverError?.type === 'validation') {
                setValidationErrors(serverError.payload);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (status === 'fetched' && data) {
            setFormValues(data);
        }
    }, [data, status]);

    if (status === 'loading') {
        return <>Loading</>;
    }
    return (
        <StyledContent>
            <h1>{createMode ? 'Create' : 'Edit'} Faq</h1>
            <InputField
                value={formValues.title}
                label="Title"
                errorMessage={validationErrors?.title}
                onChange={(e) => handleChange('title', e.target.value)}
            />
            <TextEditor
                label="Answer"
                value={formValues.answer}
                errorMessage={validationErrors?.answer}
                onChange={(value) => handleChange('answer', value)}
            />
            <InputField
                value={formValues.published}
                type="number"
                label="Published"
                errorMessage={validationErrors?.published}
                onChange={(e) => handleChange('published', e.target.value)}
            />
            <InputField
                value={formValues.sort}
                type="number"
                label="Sort"
                errorMessage={validationErrors?.sort}
                onChange={(e) => handleChange('sort', e.target.value)}
            />
            <div className="form-actions">
                <Button onClick={handleSubmit} disabled={isSubmitting}>
                    {createMode ? 'Create' : 'Save'}
                </Button>
                <BackButton />
            </div>
        </StyledContent>
    );
};

const StyledContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: 1140px;
    margin: auto;
    padding-left: 1rem;
    padding-right: 1rem;
    .form-actions {
        display: flex;
        gap: 1rem;
        margin-bottom: 5rem;
    }
`;

export default FaqDetails;
